import React, { useEffect } from 'react';
import { Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Dropzone from '../../../../../../Generic/Dropzone';
import FadeIn from '../../../../../../Animations/FadeIn/FadeIn';
import ImageUploadPreview from '../ImageUploadPreview/ImageUploadPreview';
import { uploadImage } from '../../../../../../../services/imageService/imageService';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams } from 'react-router-dom';

const Root = styled(FadeIn)(({ theme }) => ({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '30px',
	flex: 1
}));

const TitlesContainer = styled(Grid)(({ theme }) => ({
	marginTop: 10,
	marginBottom: 20,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '10px'
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: '1.7rem',
	textAlign: 'center'
}));

const SecondaryTitle = styled(Typography)(({ theme }) => ({
	maxWidth: 450,
	fontSize: '0.9rem',
	color: theme.palette.text.tertiary,
	textAlign: 'center',
	fontWeight: 300
}));

const DropzoneContainer = styled(Grid)(({ theme }) => ({
	width: '100%',
	minHeight: 200,
	flex: 1
}));

const ImagePreviewContainer = styled(Grid)(({ theme }) => ({
	width: '100%',
	gap: '10px',
	display: 'flex',
	flex: 1
}));

function UploadImages({ data, setData }) {
	const [searchParams] = useSearchParams();

	const galleryID = searchParams.get('create');
	const images = data?.images || [];
	console.log('images are: ', images);
	const uploading = images?.some((image) =>
		['progress', 'optimized']?.some((key) => key in image)
	);

	const updateImageData = (id, data) => {
		setData((prev) => {
			const updatedImages = prev?.images?.map((image) =>
				image?._id === id ? { ...image, ...data } : image
			);
			return { ...prev, images: updatedImages };
		});
	};

	/**
	 * Handles the drop event, mapping files to images with progress tracking,
	 * and initiates the image upload process.
	 * @param {File[]} files - Array of files dropped by the user.
	 */
	const onDrop = async (files) => {
		const mappedImages = files.map((file) => ({ progress: 0, file, _id: uuidv4() }));
		setData({ ...data, images: mappedImages });
		uploadImageBatches(mappedImages);
	};

	/**
	 * Uploads images in batches, with up to 4 uploads running simultaneously using Promise.all.
	 * @param {Array} images - Array of image objects with file, progress, and id properties.
	 */
	const uploadImageBatches = async (images) => {
		const batchSize = 4;
		let currentIndex = 0;

		while (currentIndex < images.length) {
			const batch = images.slice(currentIndex, currentIndex + batchSize);
			currentIndex += batchSize;

			const uploadPromises = batch.map(async (image) => {
				const optimized = await uploadImage(galleryID, image.file, (progress) =>
					updateImageData(image._id, { progress })
				);
				updateImageData(image?._id, { optimized });
			});

			await Promise.all(uploadPromises);
		}
	};

	useEffect(() => {
		console.log('images: ', images);
	}, [images]);

	return (
		<Root>
			<TitlesContainer>
				<Title>Upload your gallery images</Title>
				<SecondaryTitle>
					You can upload your gallery images in JPG or PNG format.
				</SecondaryTitle>
			</TitlesContainer>
			{!uploading && (
				<DropzoneContainer>
					<Dropzone onDrop={onDrop} />
				</DropzoneContainer>
			)}
			{uploading && (
				<ImagePreviewContainer>
					{images.map((image) => (
						<ImageUploadPreview
							key={image?._id}
							src={image?.optimized || URL.createObjectURL(image.file)}
							uploading={
								Object.keys(image)?.includes('progress') && image?.progress !== 100
							}
							progress={image.progress || 0}
						/>
					))}
				</ImagePreviewContainer>
			)}
		</Root>
	);
}

export default UploadImages;
